import { useCallback } from 'react';
import { useRouter } from 'next/router';

import '../../../consts/events.quiz-detail-cta';

import { trackEvent } from '@zep/logger/tracking';

import type { QuizSetDetailsRes } from '@/api/gen/_model/quizSetDetailsRes';
import { useUser } from '@/lib/auth';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import {
  trackSaveEntryAuthRequired,
  useShowSaveToFolderModal,
} from '@/shared/features/quiz-folder';

interface UseDuplicateHandlerParams {
  quizId: string;
  quizData: QuizSetDetailsRes;
  /** 잠긴 퀴즈의 복사 버튼인가. 흐름은 같고 진입 이벤트 이름만 다르다. */
  locked?: boolean;
}

/**
 * `내 퀴즈로 저장`(잠긴 퀴즈의 복사 포함) 핸들러.
 *
 * - 비로그인 사용자: 로그인 페이지로 보낸다(콜백 URL 포함)
 * - 로그인 사용자: 저장 위치 선택 모달을 연다
 *
 * 위치를 고르면 서버가 복사본을 만들어 그 자리에 배정한다(`POST /quizsets/{id}/copy`).
 * 잠긴 퀴즈도 그대로 저장한다 — 서버가 내 플랜 기준으로 잠긴 내 퀴즈를 만들고, 저장 전에 결제를 요구하지
 * 않는다. 그래서 잠김 여부로 흐름을 나누지 않고 옛 이벤트 이름(`enter_duplicate_locked`)만 지킨다.
 */
export const useDuplicateHandler = ({
  quizId,
  quizData,
  locked = false,
}: UseDuplicateHandlerParams) => {
  const router = useRouter();
  const { user } = useUser();
  const showSaveToFolderModal = useShowSaveToFolderModal();

  return useCallback(() => {
    trackEvent(locked ? 'enter_duplicate_locked' : 'enter_duplicate', {
      location: 'detail',
      spaceId: quizData.id,
      title: quizData.title,
      // `locked` 는 소유자 복사 버튼 구분이다. 남의 잠긴 퀴즈 저장은 `locked` 없이 오므로 퀴즈 상태를 싣는다.
      is_locked: quizData.isLocked,
    });
    // 잠긴 퀴즈 버튼은 예전부터 referrer 를 남기지 않았다. 집계 연속성을 위해 그대로 둔다.
    if (!locked) {
      setReferrer({
        from: '/quiz',
        location: 'quiz_detail_duplicate_button',
      });
    }

    // 비로그인 상태면 로그인 페이지로 리다이렉트 (로그인 후 현재 페이지로 돌아옴)
    if (!user) {
      trackSaveEntryAuthRequired('quiz_detail_cta', quizData.id);
      const currentUrl = `/quiz/${quizId}`;
      void router.push({
        pathname: '/login',
        query: { callbackUrl: currentUrl },
      });
      return;
    }

    void showSaveToFolderModal({
      id: quizData.id,
      // 게스트·뷰어의 `내 퀴즈로 저장` 과 소유자의 `복사` 를 모두 받는다. 셋 다 사본을 만든다.
      intent: 'copy',
      place: 'quiz_detail_cta',
      isQuizOwner: user.quizUserId === quizData.authorId,
    });
  }, [router, user, quizId, quizData, locked, showSaveToFolderModal]);
};
